import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { axiosInstance } from './axios';

class GatepassApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/wms/gatepass');
  }

  protected transformNode(node: any) {
    // Transform gatepass data if needed
    return node;
  }

  // Custom methods for gatepass specific operations
  async findByGatepassOrderId(gatepassOrderId: string) {
    return axiosInstance.get(`${this.baseURL}/by-order/${gatepassOrderId}`);
  }

  async submitPorterLink(id: string, porterLink: string) {
    return axiosInstance.post(
      `${GATEWAY_API_URL}/wms/gatepassorder/gatepass/${id}/mark-in-transit`,
      { porterLink },
    );
  }

  async updateStatus(id: string, status: string) {
    return axiosInstance.patch(`${this.baseURL}/${id}/status`, { status });
  }

  /**
   * What still has to be picked for a gatepass order, grouped by shelf in pick order.
   *
   * Shelf suggestions come from hourly ClickHouse data. When they are unavailable the
   * list still returns, with suggestionsAvailable=false and every SKU under `unassigned`
   * — the picker scans manually and nothing blocks.
   */
  async getPickList(gatepassOrderId: string | number) {
    const response = await axiosInstance.get(
      `${this.baseURL}/order/${gatepassOrderId}/pick-list`,
    );
    return response.data;
  }

  /** Gatepasses of a gatepass order. A GPO can have several — one per shipment. */
  async getByOrder(gatepassOrderId: string | number) {
    const response = await axiosInstance.get(`${this.baseURL}/order/${gatepassOrderId}`);
    return response.data;
  }

  /** Creates the gatepass in Unicommerce (linked to the GPO) and locally. */
  async createForOrder(gatepassOrderId: string | number) {
    const response = await axiosInstance.post(
      `${this.baseURL}/order/${gatepassOrderId}/create`,
      {},
    );
    return response.data;
  }

  /**
   * Adds one unit to the gatepass, written through to Unicommerce immediately.
   *
   * Irreversible from here: Unicommerce has no remove for non-traceable items, so a
   * mistake has to be undone in their UI and pulled back with refreshGatepass().
   */
  async scanItem(
    gatepassId: string | number,
    payload: { skuCode: string; shelfCode: string; inventoryType: string },
  ) {
    const response = await axiosInstance.post(`${this.baseURL}/${gatepassId}/scan`, payload);
    return response.data;
  }

  /** Completes in Unicommerce. Generates an invoice and closes the gatepass to scanning. */
  async completeGatepass(gatepassId: string | number) {
    const response = await axiosInstance.post(`${this.baseURL}/${gatepassId}/complete`, {});
    return response.data;
  }

  /** Mirrors Unicommerce's state back, picking up anything removed in their UI. */
  async refreshGatepass(gatepassId: string | number) {
    const response = await axiosInstance.post(`${this.baseURL}/${gatepassId}/refresh`, {});
    return response.data;
  }

  // Handle submit-porter-link action
  async 'submit-porter-link'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.gatepassId) {
      const response = await axiosInstance.post(
        `${GATEWAY_API_URL}/wms/gatepassorder/gatepass/${meta.gatepassId}/mark-in-transit`,
        payload,
      );
      return response.data;
    }
    throw new Error('gatepassId is required for submit-porter-link action');
  }

  // Handle mark-received action
  async 'mark-received'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.gatepassId) {
      const response = await axiosInstance.post(
        `${GATEWAY_API_URL}/wms/gatepassorder/gatepass/${meta.gatepassId}/mark-received`,
        payload,
      );
      return response.data;
    }
    throw new Error('gatepassId is required for mark-received action');
  }

  // Handle mark-delivered action
  async 'mark-delivered'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.gatepassId) {
      const response = await axiosInstance.post(
        `${GATEWAY_API_URL}/wms/gatepassorder/gatepass/${meta.gatepassId}/mark-delivered`,
        payload,
      );
      return response.data;
    }
    throw new Error('gatepassId is required for mark-delivered action');
  }
}

export default GatepassApiClient;
